/** Anmeldezustand. Persistiert im lokalen Speicher, damit ein Neuladen nicht abmeldet. */
import { create } from 'zustand';
import { ApiError, api, loadStoredAuth, onAuthExpired, storeAuth, type AuthResponse, type AuthUser } from '../lib/api';

interface AuthState {
  user: AuthUser | null;
  ready: boolean;
  error: string | null;
  busy: boolean;
  /**
   * Steht nach einer Registrierung: „Sieh in dein Postfach.“
   *
   * Eigenes Feld statt eines Wahrheitswerts, weil die Oberfläche die Adresse
   * mit anzeigt – bei einem Tippfehler in der Adresse ist das der einzige
   * Hinweis darauf, warum keine Mail ankommt.
   */
  pendingVerification: { email: string; message: string } | null;
  /** Gesetzt, wenn der Login an einer nicht bestätigten Adresse scheitert. */
  unverifiedEmail: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, displayName: string) => Promise<boolean>;
  /** Übernimmt ein Tokenpaar aus Bestätigung oder Passwortwechsel. */
  adopt: (result: AuthResponse) => void;
  clearPending: () => void;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  // Beim Start sofort aus dem Speicher übernehmen – kein Aufblitzen der Anmeldemaske.
  user: loadStoredAuth()?.user ?? null,
  ready: true,
  error: null,
  busy: false,
  pendingVerification: null,
  unverifiedEmail: null,

  async login(email, password) {
    set({ busy: true, error: null, unverifiedEmail: null });
    try {
      const result = await api.login({ email, password });
      storeAuth(result);
      set({ user: result.user, busy: false, pendingVerification: null });
      return true;
    } catch (error) {
      const isUnverified = error instanceof ApiError && error.status === 403;
      set({ error: (error as Error).message, busy: false, unverifiedEmail: isUnverified ? email : null });
      return false;
    }
  },

  /**
   * Meldet nicht an, sondern schaltet auf den Postfach-Hinweis um.
   *
   * Gibt deshalb `false` zurück: Die Anmeldemaske soll stehen bleiben und nicht
   * ins Spiel wechseln.
   */
  async register(email, password, displayName) {
    set({ busy: true, error: null });
    try {
      const result = await api.register({ email, password, displayName });
      set({ busy: false, pendingVerification: { email: result.email, message: result.message } });
      return false;
    } catch (error) {
      set({ error: (error as Error).message, busy: false });
      return false;
    }
  },

  adopt(result) {
    storeAuth(result);
    set({ user: result.user, error: null, busy: false, pendingVerification: null });
  },

  clearPending() {
    set({ pendingVerification: null, unverifiedEmail: null, error: null });
  },

  async logout() {
    const stored = loadStoredAuth();
    if (stored) {
      try {
        await api.logout(stored.refreshToken);
      } catch {
        /* Auch bei Serverfehler lokal abmelden. */
      }
    }
    storeAuth(null);
    set({ user: null });
  },
}));

/**
 * Räumt den Anmeldezustand ab, wenn die Sitzung serverseitig nicht mehr gilt
 * (Refresh-Token abgelaufen oder widerrufen) und `api.ts` das über
 * `onAuthExpired` meldet.
 *
 * Ohne das blieb `user` auf dem zuletzt bekannten Stand stehen: `RequireAuth`
 * hielt die Oberfläche für angemeldet, während jede Anfrage – auch "Neue
 * Partie" – nur noch mit 401 scheiterte, und es gab keinen Weg zurück zur
 * Anmeldung außer manuellem Abmelden.
 */
onAuthExpired(() => useAuth.setState({ user: null, pendingVerification: null, unverifiedEmail: null }));

/** Theme-Umschaltung (hell/dunkel), ebenfalls persistent. */
const THEME_KEY = 'hosting-sim.theme';

interface ThemeState {
  dark: boolean;
  toggle: () => void;
}

function applyTheme(dark: boolean): void {
  document.documentElement.classList.toggle('dark', dark);
  localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
}

const initialDark = (() => {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored) return stored === 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
})();
applyTheme(initialDark);

export const useTheme = create<ThemeState>((set, get) => ({
  dark: initialDark,
  toggle() {
    const next = !get().dark;
    applyTheme(next);
    set({ dark: next });
  },
}));

/**
 * Zeigt die Oberfläche alle Bereiche sofort oder lässt sie mitwachsen?
 *
 * Voreingestellt ist Mitwachsen. Wer das Spiel kennt, schaltet einmal um und
 * hat nie wieder damit zu tun – die Einstellung hängt am Gerät, nicht an der
 * Partie, weil sie eine Eigenschaft des Spielers ist und keine der Firma.
 */
const AREAS_KEY = 'hosting-sim.showAllAreas';

interface AreaState {
  showAll: boolean;
  toggle: () => void;
}

export const useAreaVisibility = create<AreaState>((set, get) => ({
  showAll: localStorage.getItem(AREAS_KEY) === 'true',
  toggle() {
    const next = !get().showAll;
    localStorage.setItem(AREAS_KEY, String(next));
    set({ showAll: next });
  },
}));
